import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell,
} from "recharts";
import { Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Iter 80 — Informe "Captación de clientes".
 *
 * Reusa los filtros de fecha del padre. Llama GET /reports/customer-acquisition
 * y muestra:
 *   - KPI total
 *   - Bar chart de distribución por origen (cuenta + %)
 *   - Line chart de evolución mensual (top 6 fuentes; el resto agrupado en "Otros")
 *   - Tabla detallada
 */
const PALETTE = [
  "#6E8B3D", "#636B2F", "#A6BC85", "#C0CFA0",
  "#525252", "#8E8E8A", "#A8A8A0", "#D6D6D2",
  "#3E462D", "#B19CD9",
];

export default function CustomerAcquisitionReport({ from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/reports/customer-acquisition?date_from=${from}&date_to=${to}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch((err) => { if (!cancel) toast.error(formatApiError(err)); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [from, to]);

  if (!data) {
    return (
      <div className="ds-card p-6 mt-6">
        <div className="text-sm text-[#525252]">{loading ? "Cargando captación de clientes…" : "Sin datos"}</div>
      </div>
    );
  }

  // Distribution chart data
  const dist = data.distribution || [];
  const total = data.total || 0;

  // Monthly evolution: pivot to recharts shape
  // Sort sources by overall count desc → top 6 + "Otros"
  const topSources = dist.slice(0, 6).map((d) => d.source);
  const monthlyChart = (data.monthly || []).map((m) => {
    const row = { month: m.month };
    let otros = 0;
    for (const [src, count] of Object.entries(m.by_source || {})) {
      if (topSources.includes(src)) row[src] = count;
      else otros += count;
    }
    if (otros > 0) row.Otros = otros;
    return row;
  });
  const lineKeys = [...topSources, ...(monthlyChart.some((m) => m.Otros) ? ["Otros"] : [])];

  return (
    <section
      className="ds-card p-5 mt-6"
      data-testid="report-customer-acquisition"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">// Iter 80</div>
          <h2 className="font-display font-bold text-lg inline-flex items-center gap-2">
            <Users size={18} /> Captación de clientes
          </h2>
          <p className="text-xs text-[#525252] mt-0.5">
            Clientes nuevos por origen en el rango {from} → {to}
          </p>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Total</div>
          <div className="font-display text-3xl font-black text-[#6E8B3D]" data-testid="report-ca-total">{total}</div>
        </div>
      </div>

      {dist.length === 0 ? (
        <div className="text-sm text-[#525252] text-center py-6">
          Sin clientes nuevos registrados en este periodo.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
                Distribución por origen
              </div>
              <ResponsiveContainer width="100%" height={260} minHeight={260}>
                <BarChart data={dist} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="source" width={140} fontSize={11} />
                  <Tooltip formatter={(v, k, ent) => [`${v} cliente${v === 1 ? "" : "s"} (${ent.payload.pct}%)`, "Captados"]} />
                  <Bar dataKey="count">
                    {dist.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
                Evolución mensual
              </div>
              {monthlyChart.length < 2 ? (
                <div className="text-xs text-[#525252] py-12 text-center border border-dashed border-[#E5E5E0]">
                  Necesitas al menos 2 meses con datos para mostrar la evolución.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260} minHeight={260}>
                  <LineChart data={monthlyChart} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
                    <XAxis dataKey="month" fontSize={11} />
                    <YAxis allowDecimals={false} fontSize={11} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {lineKeys.map((k, i) => (
                      <Line key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 3 }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="mt-5 border border-[#E5E5E0] overflow-hidden">
            <table className="w-full text-sm" data-testid="report-ca-table">
              <thead>
                <tr className="bg-[#F7F7F4] border-b border-[#E5E5E0] text-left font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                  <th className="px-3 py-2">Origen</th>
                  <th className="px-3 py-2 text-right">Captados</th>
                  <th className="px-3 py-2 text-right">% del total</th>
                </tr>
              </thead>
              <tbody>
                {dist.map((d, i) => (
                  <tr key={d.source} className="border-b border-[#F0F0EC]">
                    <td className="px-3 py-2 inline-flex items-center gap-2">
                      <span className="inline-block w-2.5 h-2.5" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
                      {d.source}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{d.count}</td>
                    <td className="px-3 py-2 text-right font-mono">{d.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
